// Deleting an organization (D83) from its page: what goes with it, the organization's name
// typed again, a reason; it runs after 7 days unless canceled. Scheduling needs a sign-in under
// an hour old. Every step is in both audit logs.
// Console D's Danger zone (APS-412, admin-d.md "Danger"): the page's last section, its confirm
// asking for the Motivo and the name typed again. Once scheduled, the section says when it runs,
// with its status word, and holds the actions an open erasure has: cancel, retry, abort.
import { Section } from '@apsio/ui'
import { useQuery } from '@tanstack/react-query'
import { type ReactNode, useId } from 'react'
import {
  abortErasure,
  cancelErasure,
  type Erasure,
  orgErasureQuery,
  retryErasure,
  STOP_CATEGORIES,
  type StopCategory,
  scheduleOrgErasure,
  useAdminMutation,
} from '../api/queries'
import { ActionForm } from '../components/ActionForm'
import { DangerZone, StatusWord } from '../components/kit'
import { ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import { IRREVERSIBLE } from './erasureParts'

/** On the organization's page, last: what deleting it takes, and the action (or the open erasure). */
/** `before`: the zone's other rows, above the erasure (a subscription's cancellation, APS-468). */
export function OrgErasurePanel({ orgId, before }: { orgId: string; before?: ReactNode }) {
  const { t } = useI18n()
  const preview = useQuery(orgErasureQuery(orgId))
  const refresh = [['org', orgId], ['erasures']]
  const schedule = useAdminMutation(scheduleOrgErasure, refresh)
  if (preview.isPending)
    return (
      <Section title={t('kit.danger.section')}>
        <Loading />
      </Section>
    )
  if (preview.isError)
    return (
      <Section title={t('kit.danger.section')}>
        <ErrorState error={preview.error} retry={() => preview.refetch()} />
      </Section>
    )
  const p = preview.data
  if (p.erasure) return <OpenErasure erasure={p.erasure} refresh={refresh} />
  // The sandbox, or one of PROTECTED_ORG_IDS such as the status page (APS-238): no Danger zone.
  if (p.protected)
    return (
      <Section title={t('kit.danger.section')}>
        <p className="dim">{t('erasure.org.protected')}</p>
      </Section>
    )
  const what = (
    <>
      {t('erasure.org.what', {
        projects: p.projects,
        apps: p.apps,
        members: p.members,
        // Apsio's own organization tells no owner (APS-238).
        kind: p.org.kind,
      })}
      {p.org.kind === 'internal' ? ` ${t('erasure.org.internal')}` : null}
    </>
  )
  return (
    <DangerZone
      before={before}
      title={t('erasure.org.title')}
      body={what}
      action={t('erasure.org.action')}
      confirmTitle={t('erasure.org.confirmTitle', { name: p.org.name })}
      confirmBody={t('erasure.org.confirmBody')}
      confirmLabel={t('erasure.org.confirm')}
      typed={p.org.name}
      noEmails
      busy={schedule.isPending}
      error={schedule.error ? <ErrorState error={schedule.error} /> : null}
      onConfirm={(reason) => schedule.mutate({ orgId, reason, confirm: p.org.name })}
    />
  )
}

/**
 * An open erasure, an organization's or a person's: its status word and when it runs, then the
 * cancel action; or, failed, staff's retry (resumes from its steps) and abort (only before
 * anything was deleted). One of a person's organizations is canceled or aborted with the person.
 */
export function OpenErasure({ erasure, refresh }: { erasure: Erasure; refresh: unknown[][] }) {
  const { t, dateTime, date } = useI18n()
  const cancel = useAdminMutation(cancelErasure, refresh)
  const retry = useAdminMutation(retryErasure, refresh)
  const abort = useAdminMutation(abortErasure, refresh)
  const steps = (erasure.steps ?? {}) as Record<string, unknown>
  const started = IRREVERSIBLE.some((step) => step in steps)
  const hint = t('erasure.reasonHint')
  return (
    <Section
      title={t('erasure.open.title')}
      meta={
        <StatusWord
          kind="erasure"
          status={erasure.status}
          until={erasure.status === 'scheduled' ? date(erasure.execute_after) : undefined}
        />
      }
    >
      <p role="status" className="er-line">
        {erasure.status === 'scheduled'
          ? // Ingest stops for an organization; nothing changes for a person until it runs.
            t(erasure.kind === 'user' ? 'erasure.scheduledForPerson' : 'erasure.scheduledFor', {
              time: dateTime(erasure.execute_after),
            })
          : erasure.status === 'failed'
            ? t('erasure.failedNow', { error: erasure.error ?? '' })
            : t('erasure.runningNow')}
      </p>
      {erasure.parent_id && erasure.status !== 'running' ? (
        <p className="dim">{t('erasure.withPerson')}</p>
      ) : null}
      <div className="er-acts">
        {erasure.status === 'scheduled' && !erasure.parent_id ? (
          <ActionForm
            label={t('erasure.cancel')}
            confirm={t('erasure.cancelConfirm')}
            reasonHint={hint}
            noEmails
            onSubmit={(reason, form) =>
              cancel.mutateAsync({ erasureId: erasure.id, reason, category: categoryOf(form) })
            }
          >
            <CategoryField />
          </ActionForm>
        ) : null}
        {erasure.status === 'failed' ? (
          <>
            <ActionForm
              label={t('erasure.retry')}
              confirm={t('erasure.retryConfirm')}
              reasonHint={hint}
              noEmails
              danger
              onSubmit={(reason) => retry.mutateAsync({ erasureId: erasure.id, reason })}
            />
            {started ? (
              <p className="dim">{t('erasure.started')}</p>
            ) : (
              <ActionForm
                label={t('erasure.abort')}
                confirm={t('erasure.abortConfirm')}
                reasonHint={hint}
                noEmails
                onSubmit={(reason, form) =>
                  abort.mutateAsync({ erasureId: erasure.id, reason, category: categoryOf(form) })
                }
              >
                <CategoryField />
              </ActionForm>
            )}
          </>
        ) : null}
      </div>
    </Section>
  )
}

const categoryOf = (form: FormData) => (form.get('category') ?? 'other') as StopCategory

/** Why it is stopped, as the email to the customer says it (the reason stays internal). */
function CategoryField() {
  const { t } = useI18n()
  const id = useId()
  return (
    <div className="field-d">
      <label className="lbl" htmlFor={id}>
        {t('erasure.category')}
      </label>
      <select id={id} name="category" className="select" defaultValue="withdrawn">
        {STOP_CATEGORIES.map((c) => (
          <option key={c} value={c}>
            {t(`erasure.category.${c}`)}
          </option>
        ))}
      </select>
    </div>
  )
}
